// Auth0 role gating EA Connect (hosted expert advisors) API access.
//
// The roles claim carries Auth0 role DISPLAY names, so this must match the
// Terraform-managed role exactly (terraform-site/auth0/*/role_ea_connect.tf).
export const EA_CONNECT_ROLE = 'EA Connect User';

// user-manager's early-access program id for EA Connect.
export const EA_CONNECT_PROGRAM = 'ea-connect';

// hasEaConnectAccess reports whether the account can actually USE EA Connect:
// the role carries ea-hosting:manage, which ea-manager requires on every
// EAHostingService RPC. Strictly the role — no admin bypass, no aliases.
//
// Without it, EaHostingGate renders EaConnectIntro (the get-started pitch +
// buy flow — checkout only needs billing-manager) instead of the product
// pages, whose every RPC would be PermissionDenied. Billing grants the role
// on purchase; the gate polls for it when a checkout closes.
//
// ACCESS, not entitlement: role-holders who have not purchased still reach
// the pages and get EaConnectIntro too (slots and billing state come from
// ea-manager's GetHostingOverview, never from the token) — see eaConnectView.
export const hasEaConnectAccess = (roles?: string[]): boolean => !!roles?.includes(EA_CONNECT_ROLE);

// canSeeEaConnect reports whether the EA Connect surfaces should be RENDERED
// AT ALL. Two surfaces read it and both must, or the rollout leaks: the
// sidebar group (menu-items/index) and the /ea-connect/* routes
// (route-guard/EaConnectGuard) — a hidden nav item alone still leaves the
// pages reachable by URL.
//
// LIMITED RELEASE: only "EA Connect User" role-holders see EA Connect at all.
// The role is hand-granted to the release cohort; holders without slots get
// the EaConnectIntro buy funnel, holders with slots get the product (see
// eaConnectView). Role-less users never reach the intro.
//
// To open the gate to everyone, return true unconditionally.
export const canSeeEaConnect = (roles?: string[]): boolean => hasEaConnectAccess(roles);

// What an EA Connect page renders, in EaHostingGate.
//   'loading'   → overview in flight
//   'intro'     → EaConnectIntro, the landing page with the buy flow
//   'product'   → the EA Connect pages
//   'suspended' → the EA Connect pages under a renew banner
export type EaConnectView = 'loading' | 'intro' | 'product' | 'suspended';

type EaConnectViewInput = {
  roles?: string[];
  // GetHostingOverview state (sections/ea-connect/useHostingOverview).
  loading: boolean;
  unavailable: boolean;
  overview: { slotLimit: number; status: string } | null;
};

// eaConnectView decides the page from the role and the SLOT LIMIT — not the
// subscription. Slots are what ea-manager enforces, and they cover both ways
// in: billing-provisioned buyers and internal accounts provisioned by hand
// (a slot limit with no subscription behind it).
//
// No slots → the landing page, even with the role: the limited-release
// cohort holds the role before buying. A suspended entitlement keeps its slot
// limit, so lapsed buyers still see their stopped bots under the banner.
//
// Overview unavailable (pre-rollout backend / transient failure) → the
// product, FAIL-OPEN: the billing plane must never lock users out of running
// bots, and the role already guarantees the hosting RPCs are callable.
export const eaConnectView = ({ roles, loading, unavailable, overview }: EaConnectViewInput): EaConnectView => {
  if (!hasEaConnectAccess(roles)) return 'intro';
  if (loading) return 'loading';
  if (unavailable || !overview) return 'product';
  if (overview.slotLimit <= 0) return 'intro';
  return overview.status === 'suspended' ? 'suspended' : 'product';
};
